import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import {
  useCartProjectionAtomValue,
  useSetAuthenticationDialogCurrentScreenAtom,
  useSetIsAuthenticationDialogAtom
} from "@RHCommerceDev/hooks/atoms";
import memoize from "@RHCommerceDev/utils/memoize";
import { useMediaQuery, Theme, Tooltip } from "@mui/material";
import { useIsCartPages } from "@RHCommerceDev/hooks/useIsCartPages";
import { StytchRHIcon } from "icons/StytchRHIcon";
import React, { FC, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { StytchInfoIcon } from "@RHCommerceDev/icon-stytch-info-icon";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import RHLink from "@RHCommerceDev/component-rh-link";
import AddToCartMembershipDialog from "@RHCommerceDev/component-rh-button-add-membership";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import { Button } from "@RHCommerceDev/unstyled-ui-components/Button";
import useCountryMessage from "@RHCommerceDev/hooks-use-country-message/useCountryMessage";
import { useCountry } from "hooks/useCountry";

interface WelcomeProps {}

const Welcome: FC<WelcomeProps> = () => {
  const cartProjection = useCartProjectionAtomValue();
  console.warn({ cartProjection });
  const { pageContent } = usePageContentNoProxy();
  const stytchData = pageContent?.emailSignupContent;
  const prefix = useLocalization();
  const countryCode = useCountry();
  const membershipMessage = useCountryMessage(countryCode);
  const [isMemberShipInCart, setIsMemberShipInCart] = useState(false);
  const setOpenAuthenticationDialog = useSetIsAuthenticationDialogAtom();

  const isCartPage = useIsCartPages();
  const isCartCheckoutPage = isCartPage.isShoppingCartPage;
  const isAddressCheckoutPage = isCartPage.isAddressPage;

  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();

  const handleMobileAuthentication = React.useCallback(() => {
    setAuthenticationDialogCurrentScreen("MobileAuthentication");
  }, [setAuthenticationDialogCurrentScreen]);

  const handleContinueClick = e => {
    e.preventDefault();

    setOpenAuthenticationDialog(false);
  };

  const form = useForm({
    defaultValues: {
      email: "",
      password: ""
    }
  });

  const desktopMatchesQuery = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xs")
  );

  const showMembershipCta = useMemo(() => {
    if (isMemberShipInCart || isCartCheckoutPage || isAddressCheckoutPage) {
      return false;
    } else {
      /** wait for cart projection to finish loading. trust me */
      if (cartProjection) {
        /** assume that is cart has items then it probably has the membership already */
        return cartProjection.totalLines === 0;
      }
    }
    return false;
  }, [
    cartProjection,
    isAddressCheckoutPage,
    isCartCheckoutPage,
    isMemberShipInCart
  ]);

  return (
    <div className="grid place-content-center">
      <div className="flex flex-col items-center justify-center">
        <div className={"mb-4 flex flex-col items-center justify-center"}>
          <Typography
            variant="h1"
            className={
              "items-baseline !gap-1 font-primary-rh-thin text-xl !text-[#000]"
            }
          >
            {stytchData?.WELCOME_TO}
            <StytchRHIcon />
          </Typography>
        </div>
        {desktopMatchesQuery ? (
          <>
            <div>
              <Typography className="font-primary-rhlight ">
                {stytchData?.Your_account_is}
              </Typography>
              <Typography className="font-primary-rhlight ">
                {stytchData?.To_enhance_account}{" "}
              </Typography>
            </div>
            <Typography className="mb-4 flex !gap-[6px]">
              <Button
                variant="link"
                onClick={handleMobileAuthentication}
                className=" ml-2 h-[15px] w-[152px] cursor-pointer !font-primary-rhlight text-xxsm !font-normal underline decoration-[0.5px] underline-offset-4 focus-visible:outline-2"
              >
                {stytchData?.Enable_Two_Factor}
              </Button>
              <Tooltip
                placement="bottom"
                arrow
                tabIndex={0}
                title={stytchData?.We_use_verification}
                classes={{
                  tooltip:
                    "bg-white text-black text-xxsm font-primary-rhlight p-5 rounded-none shadow-stytch-tooltip",
                  arrow: "!bg-white text-white transform rotate-45 !w-4 !h-4"
                }}
              >
                <div className="cursor-pointer">
                  <StytchInfoIcon />
                </div>
              </Tooltip>
            </Typography>
          </>
        ) : (
          <>
            <Typography className="font-primary-rhlight ">
              {stytchData?.Your_account_is}
            </Typography>
            <div className="mb-4">
              <div className="flex items-center whitespace-nowrap customXs:flex-col">
                <Typography className="font-primary-rhlight ">
                  {stytchData?.To_enhance_account}{" "}
                </Typography>
                <Typography className="flex !gap-[6px] ">
                  <Button
                    variant="link"
                    onClick={handleMobileAuthentication}
                    className=" ml-2  h-[15px] w-[152px] cursor-pointer !font-primary-rhlight !text-xxsm !font-normal underline decoration-[0.5px] underline-offset-4 focus-visible:outline-2"
                  >
                    {stytchData?.Enable_Two_Factor}
                  </Button>
                  <Tooltip
                    placement="bottom"
                    arrow
                    tabIndex={0}
                    title={stytchData?.We_use_verification}
                    classes={{
                      tooltip:
                        "bg-white text-black text-xxsm font-primary-rhlight p-5 rounded-none shadow-stytch-tooltip",
                      arrow:
                        "!bg-white text-white transform rotate-45 !w-4 !h-4"
                    }}
                  >
                    <div className="cursor-pointer">
                      <StytchInfoIcon />
                    </div>
                  </Tooltip>
                </Typography>
              </div>
            </div>{" "}
          </>
        )}

        <FormProvider {...form}>
          <form className="flex w-80 flex-col space-y-6">
            <Button
              type="submit"
              variant="secondary"
              onClick={handleContinueClick}
              className=" h-[48px] rounded-none py-3 font-primary-rhlight !text-[11px] tracking-[0.44px]"
            >
              {stytchData?.CONTINUE_SHOPPING}
            </Button>
          </form>
        </FormProvider>
      </div>
      {showMembershipCta && (
        <div className="mt-8 flex flex-col items-center justify-center border-t border-gray-300">
          <Typography
            variant="h1"
            className="mt-8 items-baseline !gap-1 font-primary-rh-thin !text-[#000]"
          >
            <Typography
              variant="h1"
              className={
                "items-baseline !gap-1 font-primary-rh-thin text-xl !text-[#000]"
              }
            >
              {stytchData?.JOIN_THE}{" "}
            </Typography>
            <Typography>
              <StytchRHIcon />
            </Typography>
            <Typography
              variant="h1"
              className={
                "items-baseline !gap-1 font-primary-rh-thin text-xl !text-[#000]"
              }
            >
              {stytchData?.MEMBERS_PROGRAM}
            </Typography>
          </Typography>

          <div className="mb-4 mt-4">
            <Typography variant="p" className="font-primary-rhlight">
              <div className="flex items-center text-center ">
                <span>
                  {membershipMessage}
                  <RHLink
                    to={`${prefix}/membership`}
                    underline="always"
                    variant="caption"
                    target="_blank"
                    className="!font-primary-rhlight text-xxsm text-black"
                  >
                    {stytchData?.Learn_More}
                  </RHLink>
                </span>
              </div>
            </Typography>
          </div>

          <AddToCartMembershipDialog
            isMemberShipInCart={isMemberShipInCart}
            label={
              !isMemberShipInCart
                ? stytchData?.JOIN_NOW
                : stytchData?.MEMBERSHIP_ADDED_TO_CART
            }
            setIsMemberShipInCart={setIsMemberShipInCart}
            linkType={true}
            cartType={true}
            className="text- flex h-[48px] w-full items-center justify-center rounded-none border border-black/40 py-3 font-primary-rhlight uppercase tracking-[0.44px] text-black !no-underline"
          />
        </div>
      )}
    </div>
  );
};

export default memoize(Welcome);
